import { Cascader as AntCascader, type GetRef } from 'antd';
import type { DefaultOptionType } from 'antd/es/cascader';
import {
  type ComponentProps,
  type PropsWithChildren,
  type ReactElement,
  type Ref,
  forwardRef,
} from 'react';

type RecursiveNonNullableChildrenOption<T> = Omit<T, 'children'> & {
  children?: RecursiveNonNullableChildrenOption<T>[];
};

const removeNullChildrenRecursively = <T extends { children?: T[] | null }>({
  children,
  ...rest
}: T): RecursiveNonNullableChildrenOption<T> => ({
  ...rest,
  children: children?.map(removeNullChildrenRecursively),
});

type CustomOptionType = Omit<DefaultOptionType, 'children'> & {
  children?: CustomOptionType[] | null;
};

type CustomCascaderProps<Opt extends CustomOptionType = CustomOptionType> = {
  options?: Opt[];
} & Omit<ComponentProps<typeof AntCascader>, 'options'>;

type CustomRefCascader = <Opt extends CustomOptionType = CustomOptionType>(
  props: PropsWithChildren<CustomCascaderProps<Opt>> & {
    ref?: Ref<GetRef<typeof AntCascader>>;
  },
) => ReactElement;

const CustomCascader = <Opt extends CustomOptionType = CustomOptionType>(
  { options, ...props }: CustomCascaderProps<Opt>,
  ref: Ref<GetRef<typeof AntCascader>>,
) => (
  <AntCascader
    {...(props as ComponentProps<typeof AntCascader>)}
    ref={ref}
    options={
      options?.map((option) =>
        removeNullChildrenRecursively(option as CustomOptionType),
      ) as DefaultOptionType[]
    }
  />
);

const Cascader = forwardRef(CustomCascader) as CustomRefCascader;

export { Cascader };
